import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { displayOutcomeStatus } from "Backend";
import { P } from "theme/typography";
import EditIcon from "@mui/icons-material/Edit";
import PCheckbox from "components/PCheckbox";
import { LoadingButton } from "@mui/lab";
import { LensOutcomeStatusConfigurationView, OutcomeStatus } from "generated/graphql";
import { useAppSelector } from "redux/hooks";
import {
  deleteOutcomeStatusConfig,
  selectLensEditorLensId,
  selectLensEditorLensOutcomeStatusConfiguation,
  selectLensEditorSBREnabled,
  upsertOutcomeStatusConfig,
} from "redux/slices/lensEditor";
import { SectionValueIcon } from "routes/lenses/lens/case/SectionOutcomes";
import { useFormik } from "formik";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useContext, useEffect, useState } from "react";
import { ArrowRight, Replay } from "@mui/icons-material";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";

const SectionOutcomeEditorPreview = ({
  label,
  outcomeStatus,
}: {
  label: string;
  outcomeStatus: OutcomeStatus | undefined;
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {outcomeStatus && <SectionValueIcon outcomeStatus={outcomeStatus} />}
      <P variant="xs">{label}</P>
    </Box>
  );
};
export const LensOutcomeStatusEditor = () => {
  const lensOutcomeStatuses = useAppSelector(selectLensEditorLensOutcomeStatusConfiguation);

  const lensId = useAppSelector(selectLensEditorLensId);
  const sbrEnabled = useAppSelector(selectLensEditorSBREnabled);
  const dispatch = useAppDispatchWithSnackbar();
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [pendingUpsert, setPendingUpsert] = useState(false);

  const emptyOutcomeStatusConfigs = Object.values(OutcomeStatus).map((status) => {
    const lensOutcomeStatus = lensOutcomeStatuses.find((lensStatus) => lensStatus.outcomeStatus === status);
    if (lensOutcomeStatus) {
      return lensOutcomeStatus;
    }
    return {
      outcomeStatus: status,
      customName: "",
      isHidden: false,
    };
  });
  const [editLensOutcomeStatus, setEditLensOutcomeStatus] = useState<OutcomeStatus | undefined>(undefined);
  const form = useFormik({
    initialValues: {
      key: editLensOutcomeStatus,
      customName: "",
      isHidden: false,
    },
    onSubmit: (values) => {
      if (values.key === undefined) return;
      setPendingUpsert(true);
      dispatch(
        upsertOutcomeStatusConfig({
          id: lensOutcomeStatuses.find((status) => status.outcomeStatus === values.key)?.id,
          lensId: lensId,
          outcomeStatus: values.key,
          customName: values.customName,
          isHidden: values.isHidden,
        }),
        {
          successMessage: "Lens outcome status updated",
          errorMessage: "Unable to update lens outcome status",
        }
      ).then(() => {
        setPendingUpsert(false);
        setEditLensOutcomeStatus(undefined);
      });
    },
  });

  useEffect(() => {
    if (form.values.key) {
      const lensOutcomeStatus = lensOutcomeStatuses.find((status) => status.outcomeStatus === form.values.key);
      form.setValues({
        ...form.values,
        customName: lensOutcomeStatus ? lensOutcomeStatus.customName : "",
        isHidden: lensOutcomeStatus ? lensOutcomeStatus.isHidden : false,
      });
    }
  }, [form.values.key, lensOutcomeStatuses]);

  const handleEdit = (status: OutcomeStatus) => {
    form.setValues({
      ...form.values,
      key: status,
    });
    setEditLensOutcomeStatus(status);
  };
  const handleReset = (id: number, lensOutcomeStatus: LensOutcomeStatusConfigurationView) => {
    confirmCtx.confirm({
      title: `Reset Outcome Status: ${lensOutcomeStatus.customName}?`,
      prompt: (
        <P variant="sm">
          This will reset the outcome to the default name, &quot;
          {displayOutcomeStatus(lensOutcomeStatus.outcomeStatus, sbrEnabled)}
          &quot;, and make it visible.
        </P>
      ),
      callback: () => {
        return dispatch(deleteOutcomeStatusConfig({ id }), {
          successMessage: "Lens outcome status updated",
          errorMessage: "Unable to update lens outcome status",
        });
      },
    });
  };

  return (
    <>
      <Box sx={{ my: 1 }}>
        <Card sx={{ py: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Default Name</TableCell>
                <TableCell>Custom Name</TableCell>
                <TableCell>Hidden</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emptyOutcomeStatusConfigs.map((status) => (
                <TableRow key={status.outcomeStatus}>
                  <TableCell>
                    <SectionOutcomeEditorPreview label={status.outcomeStatus} outcomeStatus={status.outcomeStatus} />
                  </TableCell>
                  <TableCell>{displayOutcomeStatus(status.outcomeStatus, sbrEnabled)}</TableCell>
                  <TableCell>
                    <P>{status.customName}</P>
                  </TableCell>
                  <TableCell>{status.isHidden ? "Hidden" : "Visible"}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => {
                          handleEdit(status.outcomeStatus);
                        }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to default">
                      <span>
                        <IconButton
                          disabled={!status.customName && !status.isHidden}
                          onClick={() => {
                            const lensOutcomeStatus = lensOutcomeStatuses.find(
                              (lensStatus) => lensStatus.outcomeStatus === status.outcomeStatus
                            );
                            if (lensOutcomeStatus) handleReset(lensOutcomeStatus.id, lensOutcomeStatus);
                          }}>
                          <Replay />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </Box>
      <Dialog
        open={!!editLensOutcomeStatus}
        onClose={() => {
          setEditLensOutcomeStatus(undefined);
        }}
        fullWidth
        maxWidth="xs">
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <P variant="xlBold">Custom Outcome Configuration</P>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}>
                <SectionOutcomeEditorPreview
                  label={displayOutcomeStatus(form.values.key, sbrEnabled)}
                  outcomeStatus={editLensOutcomeStatus}
                />
              </Box>
              <ArrowRight />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}>
                <SectionOutcomeEditorPreview
                  label={form.values.customName || displayOutcomeStatus(form.values.key, sbrEnabled)}
                  outcomeStatus={editLensOutcomeStatus}
                />
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
            <TextField
              label="Custom Name"
              name="customName"
              placeholder={displayOutcomeStatus(form.values.key, sbrEnabled)}
              value={form.values.customName}
              onChange={form.handleChange}
            />
            <PCheckbox label="Hidden" checked={form.values.isHidden} onChange={form.handleChange} name="isHidden" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              setEditLensOutcomeStatus(undefined);
            }}>
            Cancel
          </Button>
          <LoadingButton
            disabled={!form.values.key}
            loading={pendingUpsert}
            variant="contained"
            onClick={() => form.handleSubmit()}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
